<!--
  FormInput.vue

  This component displays an input with a label above it.
  It supports basic input types and is only styled for basic inputs.
  
  following are supported attributes:
    - id
    - name
    - type
    - placeholder
    - label
    - value
    - prefixLabel: If set adds a prefix label to the input field.      
    - disabled: If set (as 'disabled' or 'disabled=true' or 'disabled=false'),
                form input is rendered as disabled. Defaults to false.
    - blur: Function that will executed when the input is blurred.

  * if 'id' is not given, random generated id will be given to <input>.

  * Note: if prop data `isDateInput` is `true`, rendered input's `autocomplete` will always be `off`
        this is due to how we are using date input filed type as "text" instead of "date" due to 
        our unique date input format by end user (DD / MM / YYYY)
-->

<template>
  <div :class="prefixLabel ? 'wrapper-with-prefix' : null">
    <div class="prefixed-input" v-if="prefixLabel">
      <label>{{ prefixLabel }}</label>
    </div>
    <input
      :class="['form-input', { 'prefixed-style': prefixLabel ? true : false }, overrideClass]"
      :disabled="disabled"
      :readOnly="readOnly"
      ref="input"
      data-flatpickr-owned="true"
      :id="id"
      :maxlength="maxlength"
      :name="name"
      :placeholder="placeholder"
      :type="type"
      @blur="handleBlur"
      @keydown="handleKeyDown"
      @input="input"
      v-model="thisValue"
      v-on:keyup.enter="onEnter"
      :autocomplete="isDateInput ? 'off' : autocomplete"
    />
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import flatpickr from 'flatpickr';
import ensureDhdsMonthYearHeader from '@general/js/utils/flatpickrDhdsHeader';

export default {
  name: 'FormInput',

  computed: {
    isTemporalInput() {
      if (this.isDateInput || this.isEventDateInput) return true;

      // Support native-like input types if they are ever used by callers.
      const temporalTypes = ['date', 'datetime', 'datetime-local', 'time', 'month', 'week'];
      return temporalTypes.includes((this.type || '').toLowerCase());
    },

    shouldUseFlatpickr() {
      // Requirement: enable Flatpickr for all date/time inputs.
      // For non-temporal inputs, respect the prop.
      return this.isTemporalInput ? true : this.useFlatpickr;
    },
  },

  props: {
    overrideValue: {
      type: String,
    },

    /**
     * The value of the html input attribute autocomplete.
     * Please note that not all input type can work with this.
     * @type String
     * @values Any string value (to check what are the accepted, please check https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
     */
    autocomplete: {
      type: String,
      default: 'off',
    },

    /**
     * The value of the prefix label. Optional.
     * If set there will be a label the prefixes the input.
     * @type String
     * @values Any string value
     */
    prefixLabel: {
      type: String,
    },

    /**
     * Disabled attribute to set on the input.
     * @type Boolean
     * @values none, true, false
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * ID to set on the input and label's for attribute. If none is provided, a uuid is generated instead.
     * @type String
     * @values Any string value
     */
    id: {
      type: String,
    },

    /**
     * Flag to detect if input is date type.
     * If true, input masking for date format is enabled.
     * @type Boolean
     * @values none, true, false
     * @default false
     */
    isDateInput: {
      type: Boolean,
      default: false,
    },

    /**
     * Flag to detect if input is event date type, i.e in (YYYY / MM / DD) format
     * If true, input masking for event date format is enabled.
     * @type Boolean
     * @values true, false
     * @default false
     */
    isEventDateInput: {
      type: Boolean,
      default: false,
    },

    /**
     * If true and the input is a date input, uses Flatpickr to provide a calendar UI.
     * Keeps existing masked typing behavior when false.
     */
    useFlatpickr: {
      type: Boolean,
      default: true,
    },


    /**
     * Max length of characters for an input field.
     * @type String
     * @values Any String with a number > 0
     */
    maxlength: {
      type: String,
    },

    /**
     * ID to set on the input and label's for attribute. If none is provided, a uuid is generated instead.
     * @type String
     * @values Any string value
     */
    name: {
      type: String,
    },

    /**
     * Placeholder attribute to set on the input.
     * @type String
     * @values Any string value
     */
    placeholder: {
      type: String,
    },

    /**
     * Type to set for the input element.
     * @type String
     * @values Any input tag type values
     * @default "text"
     */
    type: {
      type: String,
      default: 'text',
    },

    /**
     * Value attribute for the input element
     * @type String
     * @values Any string
     */
    modelValue: {
      type: String,
      default: '',
    },

    /**
     * Validation object model
     * @type Object
     * @values $v
     */
    v: {
      type: Object,
    },

    /**
     * Custom class to set on the input element. Can be used to override styles.
     * @type String
     * @values Any string value
     */
    overrideClass: {
      type: String,
    },

    /**
     * If true, render input as read only input
     * @type Boolean
     * @values none, true, false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      thisId: null,
      thisValue: this.modelValue,
      flatpickrInstance: null,
    };
  },

  mounted() {
    if (this.id) {
      this.thisId = this.id;
    } else {
      this.thisId = uuid();
    }

    if (this.modelValue !== undefined && this.modelValue !== null) {
      this.thisValue = this.modelValue;
    }

    if (this.shouldUseFlatpickr && this.isTemporalInput && this.$refs.input) {
      // Used by shared SCSS to render a calendar icon inside the input.
      this.$refs.input.setAttribute('data-dhds-calendar-icon', 'true');

      const dateFormat = this.isEventDateInput ? 'Y / m / d' : 'd / m / Y';

      this.flatpickrInstance = flatpickr(this.$refs.input, {
        allowInput: true,
        dateFormat,
        disableMobile: true,
        monthSelectorType: 'static',
        onChange: (_selectedDates, dateStr) => {
          this.thisValue = dateStr;
          this.$emit('update:modelValue', dateStr);
          if (this.v) {
            this.v.$touch();
          }
        },
        onReady: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onOpen: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onMonthChange: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
        onYearChange: (_selectedDates, _dateStr, instance) => {
          ensureDhdsMonthYearHeader(instance);
        },
      });

      if (this.thisValue) {
        const parsed = flatpickr.parseDate(this.thisValue, dateFormat);
        if (parsed) {
          this.flatpickrInstance.setDate(parsed, false);
        }
      }
    }
  },

  beforeUnmount () {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
  },

  methods: {
    input($e) {
      this.thisValue = $e.target.value;
      this.$emit('update:modelValue', $e.target.value);
      if (this.v) {
        this.v.$touch();
      }
    },

    /**
     * This will emit the blur event to the parent so that it can be handled in the parent if required.
     */
    handleBlur($e) {
      this.$emit('blur', $e);
    },

    /**
     * This will emit the keydown event to the parent so that it can be handled in the parent if required.
     */
    handleKeyDown($e) {
      this.$emit('keydown', $e);
    },

    /**
     * This will emit the onEnter event to the parent so that it can be handled in the parent if required.
     */
    onEnter() {
      this.$emit('onEnter');
    },
  },

  watch: {
    /**
     * This watcher is to watch for when the parent model is changing. This will allow for the child value to be updated when the parent model value is updated outside of the child.
     */
    modelValue(val) {
      this.thisValue = val;
      if (this.flatpickrInstance) {
        const dateFormat = this.isEventDateInput ? 'Y / m / d' : 'd / m / Y';
        const parsed = val ? flatpickr.parseDate(val, dateFormat) : null;
        this.flatpickrInstance.setDate(parsed || null, false);
      }
    },

    /**
     * This will force the text input to update to a set override value
     * Only to be used as an escape hatch
     * E.g. Enable toggle PO Box on Member Details, Contact Details to update the address input
     */
    overrideValue(val) {
      this.thisValue = val;
      if (this.flatpickrInstance) {
        const dateFormat = this.isEventDateInput ? 'Y / m / d' : 'd / m / Y';
        const parsed = val ? flatpickr.parseDate(val, dateFormat) : null;
        this.flatpickrInstance.setDate(parsed || null, false);
      }
    },

    thisValue(val, oldVal) {
      if (this.shouldUseFlatpickr) {
        return val;
      }
      if (this.isDateInput && val && oldVal) {
        let newVal = val;

        if (newVal.length > oldVal.length && RegExp(/\d/).test(val[val.length - 1])) {
          if (newVal)
            if (newVal.length === 2) {
              newVal += ' / ';
            }

          if (val.replace(/(0?[0-9])+(\s\/\s)/, '').length === 2) {
            newVal += ' / ';
          }

          this.thisValue = newVal.replace(/[-. /]+/g, ' / ');

          return this.thisValue;
        }
      }

      // If isEventDateInput is true and val is not empty, it will mask date to event date format (YYYY / MM / DD)
      if (this.isEventDateInput && val && oldVal) {
        let newVal = val;

        if (newVal.length > oldVal.length && RegExp(/\d/).test(val[val.length - 1])) {
          if (newVal.length === 4) {
            newVal += ' / ';
          }

          const eventDateRegex = /(0?[0-9]{4})+(\s\/\s)/g;

          if (eventDateRegex.test(val) && val.replace(eventDateRegex, '').length === 2) {
            newVal += ' / ';
          }

          this.thisValue = newVal.replace(/[-. /]+/g, ' / ');

          return this.thisValue;
        }
      }

      return val;
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper-with-prefix {
  display: flex;
  align-items: center;
  border: 1.5px solid $PRIMARY_GREY_40;
  border-radius: 6px;
  background-color: $PRIMARY_GREY_20;
}

.prefixed-style {
  border: none !important;
  box-shadow: none !important;
  border-bottom-left-radius: 0 !important;
  border-top-left-radius: 0 !important;
  padding: 10px !important;
}

.prefixed-input {
  @include flex-row;
  @include flex-v-center;
  background-color: $PRIMARY_GREY_20;
  line-height: 3;

  label {
    width: auto;
    height: 100%;
    margin-right: 12px;
    margin-left: 12px;
    text-align: center;
  }

  input {
    margin-bottom: 0;
  }
}

.required-input {
  color: $NEUTRAL_MIDNIGHT;
}
</style>
